import React from 'react';
import { styled } from '../../styles/stitches.config';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { NAVIGATION_HEIGHT } from '../../constants/global';
import { Globe } from 'lucide-react';
import { TimezonePicker } from '../common/TimezonePicker';

export const InfoBlock = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1rem',
  borderRadius: '0.5rem', // A standard radius often looks better than a full pill when at 100% width

  fontSize: '$sm',
  lineHeight: 1.3,

  // --- 👇 The Responsive Fix ---
  boxSizing: 'border-box', // This is crucial! It includes padding/border in the total width.
  width: '100%',     
  // maxWidth: '500px',

  // --- Variants Block ---
  variants: {
    inHeader: {
      true: {
        // --- Core Flexbox Properties ---
        flexShrink: 1, // Allows this element to shrink if space is tight
        flexGrow: 1,   // Allows it to grow to fill available space
        // minWidth: 0,   // A crucial trick to allow it to shrink below its content size
        minWidth: 0,
        // --- Overrides ---
        // maxWidth: 'none', // Remove any max-width constraint from other variants
        maxWidth: '100%',    // Let flexbox determine the width
        // margin: '0 1rem', // Add some space around it
        padding: '0.5rem 1rem',
        // maxWidth: 'fit-content',
        maxHeight: `calc(${NAVIGATION_HEIGHT} - 1rem)`,
        overflow: 'auto',
        // height: '20rem',
        // justifyContent: 'center',
      }
    },

    state: {
      info: {
        backgroundColor: '$green3',
        border: '1px solid $green8',
        color: '$green11',
        // overflow: 'hidden',
        whiteSpace: 'nowrap',
  
        overflowX: 'auto', 
        overflowY: 'hidden',
        
        // 3. Ensure it doesn't overflow the parent width
        maxWidth: '100%', 

        // Optional: Hide the ugly scrollbar but keep scroll functionality
        scrollbarWidth: 'none',  // Firefox
        '&::-webkit-scrollbar': { 
          display: 'none'        // Chrome/Safari
        },

      },
      warning: {
        backgroundColor: '$yellow2',
        border: '1px solid $yellow10',

        '& div, & b, & svg': {
          color: '$yellow11',
        }
      }

    },
    fullWidth: {
      true: {
        maxWidth: '100%',
        marginBottom: '1rem'
      },
    },

    seventyFiveWidth: { 
      true: {
        maxWidth: '75%',
        marginBottom: '1rem',

        '@media (max-width: 900px)': {
          maxWidth: '100%',
        }, // <--- You were missing this comma/bracket for the Media Query
      },   // <--- Closes the 'true' object
    },

    fullWidthProfile: {
      true: {
        maxWidth: '100%',
      },
    },

    lessonHint: {
      true: {
        maxHeight: 'none',
      }
    }
  },

  

  defaultVariants: {
    state: 'info',
  },
});

const InfoIcon = styled(FiInfo, {
    /* any token from your theme works here */
    stroke: '$green8',
    flexShrink: 0,
  });

const WarningIcon = styled(FiAlertTriangle, {
  stroke: '$yellow10',
  flexShrink: 0,
})

const GlobeIcon = styled(Globe, {
  stroke: '$green8',
  flexShrink: 0,
})



export function LinkedInHint(props: any) {
  return (
    <InfoBlock inHeader {...props}>
      <InfoIcon size={16} strokeWidth={2} />
      Tip: if you have a Linkedin profile, you can use the same 400 x 400px head-shot.  
    </InfoBlock>
  );
}

export function AvailabilityTimezone({timezone, onTimezoneChange}: {timezone: string | undefined, onTimezoneChange?: (tz: string) => void}) {
  const [isEditing, setIsEditing] = React.useState(false);
  return (
    <div style={{marginBottom: '1rem'}}>
      <InfoBlock>
        <GlobeIcon size={16} strokeWidth={2} />
        <span>
          Times are in {timezone || 'your local time'}.
          {onTimezoneChange && !isEditing && (
            <span
              onClick={() => setIsEditing(true)}
              style={{ marginLeft: '0.4rem', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
            >
              Change
            </span>
          )}
        </span>
      </InfoBlock>
      {isEditing && onTimezoneChange && (
        <div style={{ marginTop: '0.5rem' }}>
          <TimezonePicker
            value={timezone || ''}
            onChange={(tz) => {
              onTimezoneChange(tz);
              setIsEditing(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

export function LessonPlanHint() {
  return (
    <InfoBlock state="info" inHeader css={{fontSize: '$md'}}>
      <InfoIcon size={18} strokeWidth={2} />
      You can rename lessons, adjust times, or remove any.   
      
   </InfoBlock>
  );
}

export function IntroCallHint({tutorName}: {tutorName: string | null}) {
  return (
    <InfoBlock state="info" inHeader lessonHint css={{fontSize: '$md'}}>
      <InfoIcon size={18} strokeWidth={2} />
      {tutorName || 'This tutor'} offers a free 15-minute introductory call where you can discuss
      your learning goals, tailor a lesson plan, and get to know their teaching style.
      
   </InfoBlock>
  );
}




export function RateHint() {
  return (
    <InfoBlock css={{maxWidth: '400px'}}>
      <InfoIcon size={16} strokeWidth={2} />
      Set your hourly rate. Typically, we suggest £30, rising to £50 for experienced tutors.
    </InfoBlock>
  );
}

export function TimelineHint() {
  return (
    <InfoBlock state="info" inHeader > {/* We will create the 'inHeader' variant next */}
      <InfoIcon size={18} />
        This is your availability calendar. Click any cell to add your free time.
    </InfoBlock>
  );
}

export function TimelineHintLarger() {
  return (
    <InfoBlock state="info" inHeader  > {/* We will create the 'inHeader' variant next */}
      <InfoIcon size={18} />
      This is your availability calendar. Click any cell to add your free time.
      </InfoBlock>
  );
}

export function TutorTip() {
  return (
    <InfoBlock seventyFiveWidth inHeader>
      <InfoIcon size={18} />
        Explore parent profiles here. Parents can, likewise, save your profile and message you.
    </InfoBlock>
  )
}

export function SubjectFilteringTip() {
  return (
    <InfoBlock seventyFiveWidth inHeader>
      <InfoIcon size={18} />
        You can only filter by subjects you teach.
    </InfoBlock>
  )
}


export function ParentTip() {
  return (
    <InfoBlock seventyFiveWidth inHeader>
      <InfoIcon size={18} />
        Explore verified tutor profiles here. Tutors can, likewise, save your profile and message you.
    </InfoBlock>
  )
}

interface WarningProps {
  missingFields?: string[];
}


export function ProfileIncompleteWarning({ missingFields = [] }: WarningProps) {
  const hasMissingFields = missingFields.length > 0;

  return (
    <InfoBlock state="warning" fullWidthProfile>
      <WarningIcon size={18} />
      <div>
        <b>Your profile is incomplete.</b>
        {hasMissingFields 
          ? ` You are missing: ${missingFields.join(', ')}.`
          : ' Please fill in all required details.'
        }
      </div>
    </InfoBlock>
  );
}

export function TableHint() {
    return (
      <InfoBlock>
        <InfoIcon size={16} strokeWidth={2} />
        You can un-tick any subject/level you don’t teach. Just click the ✓ in the table.
        </InfoBlock>
    );
  }



  export function TableHintSmall({ children, ...props }: any) {
    return (
      <InfoBlock inHeader {...props}>
        <InfoIcon size={16} strokeWidth={2} />
        {children ?? 'You can un-tick any subject/level you don’t teach. Just click the ✓ in the table.'}
        </InfoBlock>
    );
  }
